import {
    Button,
    IconButton,
    isNotNullOrUndefined,
    LoadingSkeleton,
    RoundImage,
    tokens,
    UploadErrorAlert,
    useDownloadFileParameterized,
} from 'ComponentLibrary'
import { Box, Button as MuiButton, Link, Stack, useTheme } from '@mui/material'
import { X } from 'react-feather'
import { DownloadSimple as Download, Image, ImageIcon } from '@phosphor-icons/react'
import { styled } from '@mui/material/styles'
import { RefObject, useMemo } from 'react'
import { isEmpty } from 'lodash'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { hasTiffContentType, hasTiffExtension } from 'ComponentLibrary/src/common/files'
import { Typography } from 'ComponentLibrary/src/piazza/text'

type IndexedImage = Image & {
    originalIndex: number
}

type Image = {
    uuid: string
    name: string
    url: string
    thumbnail_url?: string
    content_type?: string
}

type ImageGalleryProps = {
    images: Image[]
    isUploading?: boolean
    numImagesToUpload?: number
    uploadImages?: (images: File[]) => void
    deleteImage?: (imageIndex: number) => void
    failedImageUploads?: string[]
    uploadImageRef?: RefObject<HTMLLabelElement | null>
    size?: 'small' | 'medium'
    orderId?: string
    generateZipUrl?: () => void
    isGeneratingImagesZipUrl?: boolean
}

export const ImageGallery = ({
    images,
    uploadImages,
    deleteImage,
    isUploading,
    numImagesToUpload,
    failedImageUploads,
    uploadImageRef,
    size = 'medium',
    orderId,
    generateZipUrl,
    isGeneratingImagesZipUrl,
}: ImageGalleryProps) => {
    const theme = useTheme()

    const indexedImages: IndexedImage[] = useMemo(
        () =>
            images.map((image, originalIndex) => ({
                ...image,
                originalIndex,
            })),
        [images]
    )

    const imagesWithoutTiff = useMemo(
        () => indexedImages.filter((image) => !(hasTiffContentType(image.content_type) || hasTiffExtension(image.name))),
        [indexedImages]
    )
    const imagesOnlyTiff = useMemo(
        () => indexedImages.filter((image) => hasTiffContentType(image.content_type) || hasTiffExtension(image.name)),
        [indexedImages]
    )

    const downloadTiffImage = useDownloadFileParameterized()

    return (
        <Stack direction="column" gap={theme.spacing(2)}>
            {failedImageUploads && failedImageUploads.length > 0 && <UploadErrorAlert failedUploads={failedImageUploads} />}
            {(!isEmpty(images) || isUploading) && (
                <Stack direction="row" gap={size === 'small' ? theme.spacing(2) : theme.spacing(5)} flexWrap="wrap">
                    {imagesWithoutTiff.map(({ url, thumbnail_url, uuid, name, originalIndex }) => (
                        <ImageBox key={uuid} style={{ position: 'relative' }}>
                            <Stack direction="row" gap={theme.spacing(2)} position="absolute" top={6} right={6}>
                                <Link href={url} download>
                                    <DownloadButton icon={<Download />} />
                                </Link>
                                {deleteImage && (
                                    <CancelButton
                                        icon={<X />}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            deleteImage(originalIndex)
                                        }}
                                    />
                                )}
                            </Stack>
                            <RoundImage
                                src={thumbnail_url}
                                fallbackImageSrc={[url].filter(isNotNullOrUndefined)}
                                height={size === 'small' ? '72px' : '102px'}
                                width={size === 'small' ? '72px' : '128px'}
                                alt={name}
                            />
                        </ImageBox>
                    ))}
                    {isUploading && numImagesToUpload && (
                        <LoadingSkeleton
                            count={numImagesToUpload}
                            height={size === 'small' ? '72px' : '102px'}
                            width={size === 'small' ? '72px' : '128px'}
                            sx={{ transform: 'none' }}
                        />
                    )}
                </Stack>
            )}
            {!isEmpty(imagesOnlyTiff) && (
                <Stack direction="column" gap={theme.spacing(2)} flexWrap="wrap">
                    {imagesOnlyTiff.map((imageOnlyTiff) => (
                        <Stack
                            direction="row"
                            key={`${imageOnlyTiff.originalIndex}`}
                            justifyContent="space-between"
                            alignItems="center"
                            height="28px"
                            sx={{
                                '&:hover': {
                                    '& .action-container': {
                                        opacity: 1,
                                    },
                                },
                            }}
                        >
                            <Button
                                variant="text"
                                label={imageOnlyTiff.name}
                                startIcon={<Image size={24} color={theme.palette.customtext.darkblue100} />}
                                onClick={async () => {
                                    const downloadCallback = downloadTiffImage({
                                        url: imageOnlyTiff.url,
                                        name: imageOnlyTiff.name,
                                    })
                                    await downloadCallback
                                }}
                                style={{
                                    textTransform: 'none',
                                    color: theme.palette.customtext.black100,
                                    paddingLeft: 0,
                                }}
                            />
                            <Stack
                                className="action-container"
                                direction="row"
                                gap={theme.spacing(2)}
                                alignItems="center"
                                sx={{
                                    opacity: 0,
                                    width: '28px',
                                }}
                            >
                                {deleteImage && (
                                    <CancelButton
                                        icon={<X />}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            deleteImage(imageOnlyTiff.originalIndex)
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            )}
            <Stack direction="row" spacing={2} alignItems="center">
                {uploadImages && (
                    <UploadButton
                        ref={uploadImageRef}
                        disableElevation
                        disableRipple
                        component="label"
                        variant="contained"
                        startIcon={<ImageIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconBoldest} />}
                    >
                        <Typography variant="link">Bilder hochladen</Typography>
                        <input
                            hidden
                            multiple
                            onChange={(e) => {
                                e.target.files && uploadImages(Array.from(e.target.files))
                                e.target.value = '' // This resets the input and makes sure the onChange triggers again even if we upload the same file again
                            }}
                            id="contained-button-file"
                            type="file"
                            accept={'.png,.jpg,.jpeg,.svg,.tif,.tiff'}
                        />
                    </UploadButton>
                )}

                {images?.length > 1 && orderId && generateZipUrl && (
                    <Button
                        style={{ color: theme.palette.customtext.black100, textTransform: 'none', fontWeight: 400 }}
                        variant="secondary"
                        size="medium"
                        label={'Alle Bilder herunterladen'}
                        startIcon={
                            isGeneratingImagesZipUrl ? (
                                <LoadingIndicator isPending />
                            ) : (
                                <Download color={`${theme.palette.customtext.black54}`} />
                            )
                        }
                        onClick={generateZipUrl}
                        disabled={isGeneratingImagesZipUrl}
                    />
                )}
            </Stack>
        </Stack>
    )
}

const UploadButton = styled(MuiButton)(() => ({
    width: 'fit-content',
    lineHeight: 0,
    height: '32px',
    borderRadius: tokens.SizingCornerS,
    color: tokens.ColorTextDefault,
    backgroundColor: tokens.ColorSurfaceDefault,
    paddingLeft: tokens.SizingSpacing150,
    paddingRight: tokens.SizingSpacing150,
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
})) as typeof MuiButton

const CancelButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: theme.palette.white,
    opacity: 0.8,
    borderRadius: '5px',
    padding: '1px',
    '&:hover': {
        opacity: 0.95,
        color: theme.palette.customtext.black100,
    },
}))

const DownloadButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: theme.palette.white,
    opacity: 0.8,
    borderRadius: '5px',
    padding: '1px',
    '&:hover': {
        opacity: 0.95,
        color: theme.palette.customtext.black100,
    },
}))

const ImageBox = styled(Box)(() => ({
    '& button': {
        display: 'none',
    },
    '&:hover': {
        '& button': {
            display: 'flex',
        },
    },
}))
